import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Stack, Typography } from '@mui/material'
import { Card, Button, Switch } from '@repo/ui'
import { ArrowUpRightIcon, Lock } from 'lucide-react'
import { ROUTES } from 'routes'
import { useSnackbar } from 'contexts'
import { useReferrerState } from 'hooks'
import {
  useCanWhiteLabel,
  useCurrentUserCompany,
  useCertificateTriggerSettings,
  useUpdateWhiteLabelSetting,
} from 'hooks/api'
import { getErrorMessage } from 'utils'

/**
 * White-label card (Studio). Like Billing's activation control, this is a REAL
 * in-place toggle (edit it here) PLUS a link to the canonical setting on the
 * Features page — the two read/write the same persisted `hide_powered_by`
 * setting (TD-066), so they stay in sync after a refetch.
 *
 * Entitlement is real via `useCanWhiteLabel`, gated to `WHITE_LABEL_TIER_KEYS`
 * (`packages/types/src/billing/plan-tier.ts` — Scale + Enterprise). Fixed
 * 2026-08-30: this card hardcoded "Available on Enterprise" even though the
 * actual gate has included Scale for a while (FeaturesPage.tsx already showed
 * the correct dynamic tier via the same hook's `minimumPlanTitle`) — this was
 * telling Scale customers the feature needed a plan they already had.
 * ⚠️ `context/decisions/0004-plan-packaging.md` (ADR 0004) still documents
 * "Enterprise only" for this entitlement — that ADR is now stale against
 * shipped code and hasn't been reconciled; flagging rather than silently
 * resolving which one is the intended source of truth.
 */
interface WhiteLabelCardProps {
  /** Which surface the attribution sits on (drives the description copy). */
  surface?: 'email' | 'certificate'
  /**
   * VERA-644 (TD-066 last-mile): bubbles the local (possibly unsaved) toggle
   * state up to the parent so the certificate-tab live iframe preview can
   * reflect it without waiting for a save. Called on mount/sync and on every
   * toggle.
   */
  onHidePoweredByChange?: (hidePoweredBy: boolean) => void
}

export const WhiteLabelCard = ({ surface = 'email', onHidePoweredByChange }: WhiteLabelCardProps) => {
  const navigate = useNavigate()
  // Carries "← Back to Studio" to whichever settings page this card links out to.
  const referrer = useReferrerState('Studio')
  const { open: notification } = useSnackbar()
  const { canWhiteLabel, minimumPlanTitle: whiteLabelMinPlan } = useCanWhiteLabel()

  const companyQuery = useCurrentUserCompany()
  const companyId = companyQuery.data?.id ?? ''

  const { settings } = useCertificateTriggerSettings(companyId, {
    enabled: !!companyId,
  })
  const savedHidePoweredBy = settings?.hide_powered_by ?? false

  const whiteLabelMutation = useUpdateWhiteLabelSetting()

  const [hidePoweredBy, setHidePoweredBy] = useState(savedHidePoweredBy)

  // Sync local toggle when the persisted value loads/changes (e.g. updated
  // from the Features page).
  useEffect(() => {
    setHidePoweredBy(savedHidePoweredBy)
  }, [savedHidePoweredBy])

  // Bubble every local value (initial sync + toggles) up to the parent so the
  // live preview iframe can mirror it (VERA-644 / TD-066 last-mile).
  useEffect(() => {
    onHidePoweredByChange?.(hidePoweredBy)
  }, [hidePoweredBy, onHidePoweredByChange])

  const handleChange = (checked: boolean) => {
    if (!companyId) return
    setHidePoweredBy(checked) // optimistic
    whiteLabelMutation.mutate(
      { companyId, hide_powered_by: checked },
      {
        // No success toast: the switch already flipped optimistically above.
        onError: error => {
          setHidePoweredBy(savedHidePoweredBy) // revert on failure
          notification({ type: 'error', title: getErrorMessage(error) })
        },
      }
    )
  }

  // The per-surface line describes ONLY the panel this card is embedded in
  // (certificate preview vs. email preview) — SMS isn't a Studio preview
  // surface, so it isn't added to `where`. It's still mentioned once, below,
  // alongside the entitlement note, so a visitor on either panel learns the
  // toggle's full reach rather than just the one channel they're looking at.
  const where = surface === 'certificate' ? 'certificate page' : 'email footer'

  const goToFeatures = () => navigate(ROUTES.SETTINGS_COMPANY_FEATURES, { state: referrer })

  return (
    <Card title="White label" spacing={4}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={4}
      >
        <Stack sx={{ pr: 2 }} spacing={1}>
          <Typography variant="body2" color="text.primary">
            Hide &ldquo;Powered by Vera&rdquo;
          </Typography>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Remove the Vera attribution from your {where} — and from every SMS,
            which drops the &ldquo;(via vera.inc)&rdquo; qualifier and leads with just your
            name.
            {!canWhiteLabel &&
              ` Available on the ${whiteLabelMinPlan ?? 'Scale'} plan and higher.`}
          </Typography>
          {canWhiteLabel && (
            // Parity with the activation dropdown's "Learn more in Features" item
            // (body4, weight 500, text.primary, spacing 2, up-right arrow).
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              onClick={goToFeatures}
              sx={{
                cursor: 'pointer',
                width: 'fit-content',
                pt: 1,
                color: 'text.primary',
                '&:hover': { opacity: 0.7 },
              }}
            >
              <Typography variant="body4" fontWeight={500} color="text.primary">
                Manage in Features
              </Typography>
              <ArrowUpRightIcon size={14} strokeWidth={1.5} />
            </Stack>
          )}
        </Stack>

        {canWhiteLabel ? (
          <Switch
            checked={hidePoweredBy}
            disabled={whiteLabelMutation.isPending}
            onChange={(_e, checked) => handleChange(checked)}
          />
        ) : (
          // Not entitled → the same upgrade flow as Billing → "Change plan".
          <Button
            variant="secondary"
            disableElevation
            startIcon={<Lock size={14} />}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            onClick={() => navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN, { state: referrer })}
          >
            Upgrade to unlock
          </Button>
        )}
      </Stack>
    </Card>
  )
}
